import { useEffect, useState, type CSSProperties, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { ApiError } from '../lib/api'

// Self-serve company signup. Creates the company + you as its admin, then drops
// you straight into the dashboard with the get-started checklist.
export function RegisterPage() {
  const { user, loading, register } = useAuth()
  const navigate = useNavigate()
  const [companyName, setCompanyName] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === 'super_admin' ? '/admin' : '/app/dashboard', { replace: true })
    }
  }, [loading, user, navigate])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    setSubmitting(true)
    try {
      await register({ companyName, name, email, password })
      navigate('/app/dashboard', { replace: true })
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center', padding: 24 }}>
      <form className="card" style={{ width: 400, maxWidth: '100%' }} onSubmit={onSubmit}>
        <img
          src="/logo-lockup.png"
          alt="Resilience — Damage Mitigation & Risk Management"
          style={{ height: 56, width: 'auto', display: 'block', marginBottom: 8 }}
        />
        <p className="page-sub" style={{ marginTop: 4 }}>Create your company account</p>

        <label style={labelStyle}>Company name</label>
        <input
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Acme Builders"
          style={inputStyle}
          required
        />

        <label style={{ ...labelStyle, marginTop: 14 }}>Your name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} required />

        <label style={{ ...labelStyle, marginTop: 14 }}>Work email</label>
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          style={inputStyle}
          required
        />

        <label style={{ ...labelStyle, marginTop: 14 }}>Password</label>
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          minLength={8}
          style={inputStyle}
          required
        />

        {error && <p className="form-error">{error}</p>}

        <button
          className="btn btn-primary"
          type="submit"
          disabled={submitting}
          style={{ width: '100%', marginTop: 18, opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? 'Creating your account…' : 'Create account'}
        </button>

        <p style={{ marginTop: 14, fontSize: 13, color: 'var(--gray)', textAlign: 'center' }}>
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      </form>
    </div>
  )
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'email_taken':
        return 'That email already has an account. Try logging in instead.'
      case 'weak_password':
        return 'Password must be at least 8 characters.'
      case 'invalid_email':
        return 'Enter a valid email address.'
      case 'missing_fields':
        return 'Please fill in every field.'
    }
  }
  return 'Could not create your account. Please try again.'
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 6,
}
const inputStyle: CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  font: 'inherit',
  background: 'var(--bg)',
}
